import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { WallSquareState } from "@gooner/shared";
import {
  WALL_DEFS,
  WALL_UPLOAD_MAX_SECONDS,
  centsLabel,
  isGoldTier,
  wallIdOfRegion,
  wallSettingsFor,
} from "@gooner/shared";
import { apiErrorOf, userApi } from "../lib/api";
import { useSession } from "../lib/session";
import { usePublicSettings } from "../lib/publicSettings";
import {
  FAPS_WALL_AUTH_REASON,
  trackFapsGetMoreClicked,
  trackFapsInsufficientShown,
  useFaps,
  useFapsStore,
} from "../lib/faps";
import { track } from "../analytics";
import { trackGoldGate } from "../lib/gold";
import { AuthModal } from "../control/components/AuthModal";
import { FapsDialog } from "../control/components/FapsDialog";
import { GoldDialog } from "../control/components/GoldDialog";
import { CROWN_GOLD } from "../control/ui/Crown";
import { FapsAmount } from "../control/ui/FapsAmount";
import { InsufficientFaps } from "../components/InsufficientFaps";
import { MediaPicker, type MediaPick, isUploadPick, pickId } from "../components/MediaPicker";
import { GoldName } from "../control/ui/Gold";
import { IconClose } from "../control/ui/icons";
import { cx } from "../control/ui/primitives";

const MAX_PICKS = 5;

/**
 * Taking a square — GoldDialog's skeleton (portal, z-[60], panel card) around a
 * different sale: one square, its live price, and the 1–5 library clips the buyer
 * wants on it.
 *
 * WHAT THE PRICE MEANS is said plainly: pay-to-place, hold until outbid, a takeover
 * price that rides demand, no refunds. Every sentence in the fine print maps to a rule
 * the server actually enforces — a money dialog that over-promises writes support
 * tickets.
 *
 * Clips are sent as PUBLIC tokens (clipKey — the catalog already speaks them); the
 * server resolves and re-vets every pick, so nothing here is trusted. The email is
 * the receipt address, straight to the processor, never stored — gold's rule, said
 * with gold's sentence.
 *
 * IN FAPS (`currency === "faps"`) the same dialog, a different till: the price is the
 * same NUMBER spelled in FAPS (1 FAP = 1¢), there is no email because there is no
 * processor, the buyer's balance sits above the button, and a short balance turns the
 * form into three doors — the chest, a code, Premium — rather than a refusal. The
 * server still computes every price and answers the 409 with how far short.
 */
export function WallBuyDialog({
  regionId,
  square,
  selling,
  currency = "cash",
  premiumOnly,
  onClose,
}: {
  regionId: string;
  /** The square's live state from the board poll, or null while it loads. */
  square: WallSquareState | null;
  /** wall.enabled AND a way to pay — the board's own `enabled` flag. */
  selling: boolean;
  /** The board's own `currency`. */
  currency?: "faps" | "cash";
  /** Claiming here is a GoonLords Premium privilege — the Premium Wall's gate. */
  premiumOnly: boolean;
  onClose: () => void;
}) {
  const { user, ready } = useSession();
  const [authOpen, setAuthOpen] = useState(false);
  const faps = currency === "faps";
  const wallId = wallIdOfRegion(regionId) ?? "main";
  const wallName = WALL_DEFS[wallId].name;
  const settings = usePublicSettings();
  // Defending is for the HOLDER (you), a FAPS sink, gated by the wall's own switch. When it
  // is on and this square is yours, the form below becomes a Defend button instead of a
  // takeover. `isMine` is the holder's name against the session's — a square lives on an
  // account, so the name is the identity here.
  const isMine = !!user && square?.status === "taken" && square.holder?.username === user.username;
  const canDefend =
    isMine && faps && selling && !!settings && wallSettingsFor(settings, wallId).defendEnabled;

  /*
   * THE GATE, asked of the session and nothing else. `useGoldGate().gold` reads
   * PERMISSIVE — false — until /api/settings answers, and a gate on it would flash the
   * upsell at a paying member on every open. Membership needs no settings: it is the
   * timestamp on the user, the same arithmetic GoldDialog itself uses. `ready` keeps
   * the frame before the session loads from deciding anything.
   *
   * A signed-out visitor is gated too: GoldDialog's own "Sign in to go Premium" is the
   * right first step for them, where this dialog's sign-in prompt would promise a square
   * they still could not claim. Staleness — a membership that lapsed between the board
   * loading and the tap — is the server's 403 + goldUpsell, caught in BuyForm below.
   */
  const goldNow = isGoldTier({
    owner: user?.id ?? null,
    goldUntil: user?.goldUntil ?? null,
    capsEnabled: false,
    now: Date.now(),
  });
  const gated = premiumOnly && ready && !goldNow;
  const [upsell, setUpsell] = useState(false);
  useEffect(() => {
    if (gated || upsell) trackGoldGate("premium_wall");
  }, [gated, upsell]);

  useEffect(() => {
    track("wall_buy_opened", { tier: square?.tier, status: square?.status, wall: wallId });
    // Impression-on-mount, the GoldDialog precedent — dependencies deliberately empty:
    // one open is one impression, however the poll refreshes the square underneath.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const price = square ? (
    faps ? <FapsAmount n={square.minBidCents} size={13} gold={false} /> : centsLabel(square.minBidCents)
  ) : null;
  const taken = square?.status === "taken";
  // The board can't personalise "Free" away for a capped account (/wall/state is
  // public), so the 409 at checkout flips this dialog to the paid rendering instead.
  const [freeDenied, setFreeDenied] = useState(false);
  const free = square?.freeClaim === true && !freeDenied;

  if (gated || upsell) return <GoldDialog source="premium_wall" onClose={onClose} />;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-6 shadow-2xl"
      >
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-zinc-100">
            {canDefend ? "Defend this square" : taken ? "Take over this square" : "Take this square"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-zinc-400 hover:bg-[var(--color-panel-2)] hover:text-white"
          >
            <IconClose />
          </button>
        </div>
        <p className="mb-4 text-sm text-zinc-400">
          {square ? (
            <>
              A <span className="text-zinc-200">{square.tier}</span> square on the {wallName}
              {isMine ? (
                <> — yours.</>
              ) : (
                <>
                  {taken ? (
                    <>
                      {" "}
                      — held by{" "}
                      <GoldName
                        className={cx(
                          "font-medium",
                          square.holder?.gold ? "text-[var(--color-accent-soft)]" : "text-zinc-200",
                        )}
                        name={`@${square.holder?.username}`}
                        gold={square.holder?.gold}
                      />
                    </>
                  ) : null}
                  . Yours{" "}
                  {free ? (
                    <span className="font-semibold text-zinc-100">free</span>
                  ) : (
                    <>
                      for <span className="font-semibold text-zinc-100">{price}</span>
                    </>
                  )}
                  .
                </>
              )}
            </>
          ) : (
            "Loading this square…"
          )}
        </p>

        {!ready ? null : !user ? (
          <>
            <button
              type="button"
              onClick={() => setAuthOpen(true)}
              className="w-full rounded-lg bg-[var(--color-accent)] px-4 py-2.5 text-center text-sm font-semibold text-white hover:opacity-90"
            >
              Sign in to buy a square
            </button>
            <p className="mt-2 text-xs text-zinc-500">
              {faps ? FAPS_WALL_AUTH_REASON : "A square carries its owner's name, so it lives on an account."}
            </p>
          </>
        ) : !selling ? (
          <p className="rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel-2)] px-3 py-2 text-sm text-zinc-400">
            Squares aren't on sale quite yet. The wall is warming up.
          </p>
        ) : canDefend && square ? (
          <DefendPanel regionId={regionId} square={square} onClose={onClose} />
        ) : square ? (
          <BuyForm
            regionId={regionId}
            square={square}
            free={free}
            faps={faps}
            onFreeDenied={() => setFreeDenied(true)}
            onUpsell={() => setUpsell(true)}
          />
        ) : null}

        {authOpen ? (
          <AuthModal initialMode="register" reason={faps ? FAPS_WALL_AUTH_REASON : undefined} onClose={() => setAuthOpen(false)} />
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

function BuyForm({
  regionId,
  square,
  free,
  faps,
  onFreeDenied,
  onUpsell,
}: {
  regionId: string;
  square: WallSquareState;
  /** The dialog's word, not the square's — already false again after a cap 409. */
  free: boolean;
  /** The board sells in FAPS: no email, the balance above the button, doors when short. */
  faps: boolean;
  onFreeDenied: () => void;
  /** The server said Premium would lift this refusal (a membership that lapsed under
   *  an open dialog) — the host swaps in the Premium dialog. */
  onUpsell: () => void;
}) {
  const wallet = useFaps();
  // How far short the buyer is, when the server (or the pre-empt) said so. Null = not
  // short, or not known to be. The pre-empt is best-effort UX (the sign-in-prompt
  // precedent): the 409 is always the backstop, and it carries the same number.
  const [short, setShort] = useState<number | null>(null);
  const [fapsOpen, setFapsOpen] = useState<"chest" | null>(null);
  const [goldOpen, setGoldOpen] = useState(false);
  useEffect(() => {
    // A balance that grew (the chest opened, a code redeemed) un-shorts the form.
    if (short !== null && wallet.balance !== null && wallet.balance >= square.minBidCents) setShort(null);
  }, [wallet.balance, short, square.minBidCents]);
  const wallId = wallIdOfRegion(regionId) ?? "main";
  // The picks. The tray that fills them is MediaPicker (2026-09-13: lifted out of here
  // for the Feed, which chooses a post's clip through the very same shelves); this form
  // keeps what the wall alone cares about — how many, the length rule, and the sale.
  const [picked, setPicked] = useState<MediaPick[]>([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buy = async () => {
    setError(null);
    // The FAPS pre-empt: a balance this tab already knows is short turns the form into
    // its doors without a request. The server's 409 below catches the rest.
    if (faps && wallet.balance !== null && wallet.balance < square.minBidCents) {
      const needed = square.minBidCents - wallet.balance;
      trackFapsInsufficientShown(needed);
      setShort(needed);
      return;
    }
    setBusy(true);
    try {
      const uploads = picked.filter(isUploadPick);
      track("wall_checkout_started", {
        tier: square.tier,
        clips: picked.length,
        uploads: uploads.length,
        wall: wallId,
      });
      const res = await userApi.wallCheckout(
        regionId,
        picked.filter((p) => !isUploadPick(p)).map(pickId),
        uploads.map(pickId),
        faps ? "" : email.trim(),
      );
      if ("claimed" in res) {
        // A free claim or a FAPS purchase is already live — no processor leg. Same
        // landing page as a payment so the "your square is live" moment reads
        // identically; the query says which, for the interim copy. The server captured
        // the sale (wall_square_bought_faps) — the client never claims one.
        if (faps) {
          if (res.fapsBalance !== undefined) useFapsStore.getState().applyClick({ fapsEarned: 0, fapsBalance: res.fapsBalance });
        } else {
          track("wall_free_claimed", { tier: square.tier, wall: wallId });
        }
        window.location.href = `/wall/thanks?region=${encodeURIComponent(res.regionId)}&${faps ? "faps" : "free"}=1`;
        return;
      }
      // Their checkout, their domain — replaced, not a new tab, so the return url
      // lands back in this window with the session still in it (GoldDialog's rule).
      window.location.href = res.checkoutUrl;
    } catch (err) {
      setBusy(false);
      // The server's sentence, minus the status prefix — the 423 countdown and the
      // clip refusals are written to be shown.
      const msg = err instanceof Error ? err.message.replace(/^\d+:\s*/, "") : "";
      // Counted by STATUS, which is the diagnosis: 423 contention, 400 picker bug,
      // 5xx processor. Never the sentence — it can carry a countdown.
      const status = err instanceof Error ? Number(/^(\d{3})/.exec(err.message)?.[1]) : undefined;
      track("wall_checkout_failed", { tier: square.tier, status: status || 0, wall: wallId });
      // The typed flag FIRST, before any status is read: Premium would lift this
      // refusal (the Premium Wall's gate, met by a membership that lapsed under the
      // open dialog), and the answer is the Premium dialog, not a sentence.
      if (apiErrorOf(err)?.goldUpsell) {
        onUpsell();
        return;
      }
      // The daily-freebie 409: show the sentence AND fall back to the paid form —
      // the badge on the glass could not know this account's freebie was spent.
      if (free && status === 409) onFreeDenied();
      // The short-balance 409 carries `needed`: the doors, not the sentence.
      const needed = (apiErrorOf(err) as { needed?: number } | undefined)?.needed;
      if (faps && status === 409 && typeof needed === "number") {
        trackFapsInsufficientShown(needed);
        setShort(needed);
        return;
      }
      setError(msg || (faps ? "Could not buy the square. Try again." : "Could not start checkout. Check the email and try again."));
    }
  };

  return (
    <>
      <p className="mb-1 text-sm font-medium text-zinc-200">
        Pick 1–{MAX_PICKS} clips for your square
        <span className="ml-2 text-xs font-normal text-zinc-500">
          {picked.length} picked — the square loops through them
        </span>
      </p>
      <MediaPicker
        picks={picked}
        onChange={setPicked}
        max={MAX_PICKS}
        maxSeconds={WALL_UPLOAD_MAX_SECONDS}
        tooLongCopy="Too long for a square"
        uploadNote="Your square goes up straight away on your other picks meanwhile."
        onTab={(tab) => track("wall_picker_tab", { tab, wall: wallId })}
        onUploadStarted={(bytes) => track("wall_upload_started", { bytes, wall: wallId })}
      />

      {/* No payment, no receipt — the free path and the FAPS path never show the email
          field, and the server never reads it there. */}
      {free || faps ? null : (
        <>
          <label className="mt-4 block">
            <span className="mb-1 block text-xs text-zinc-400">Email for your receipt</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              placeholder="you@example.com"
              autoComplete="email"
              className="w-full rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel-2)] px-3 py-2 text-base text-zinc-100 placeholder:text-zinc-600 sm:text-sm"
            />
          </label>
          <p className="mt-1 text-[11px] leading-snug text-zinc-500">
            This is for only sending your receipt to you.
          </p>
        </>
      )}

      {/* The buyer's balance, above the button, so the price and the purse sit together. */}
      {faps ? (
        <p className="mt-4 flex items-center gap-1.5 text-xs text-zinc-400">
          You have{" "}
          {wallet.balance === null ? (
            <span className="font-semibold" style={{ color: CROWN_GOLD }}>····</span>
          ) : (
            <FapsAmount n={wallet.balance} className="font-semibold" />
          )}
          {wallet.balance !== null && wallet.balance < square.minBidCents ? (
            <span className="inline-flex items-center gap-1 text-zinc-500">
              — <FapsAmount n={square.minBidCents - wallet.balance} size={11} gold={false} /> short
            </span>
          ) : null}
        </p>
      ) : null}

      {short !== null ? (
        <InsufficientFaps
          needed={short}
          chestReady={wallet.chestReady}
          onDoor={(door) => {
            trackFapsGetMoreClicked(door);
            if (door === "premium") setGoldOpen(true);
            else setFapsOpen("chest");
          }}
        />
      ) : (
        <button
          type="button"
          disabled={busy || picked.length === 0}
          onClick={() => void buy()}
          className="mt-3 w-full rounded-lg bg-[var(--color-accent)] px-4 py-2.5 text-center text-sm font-semibold text-black transition-opacity enabled:hover:opacity-90 disabled:opacity-50"
        >
          {busy
            ? free
              ? "Claiming your square…"
              : faps
                ? "Buying your square…"
                : "Taking you to checkout…"
            : free
              ? "Claim this square — free"
              : faps
                ? (
                    <span className="inline-flex items-center gap-1">
                      {square.status === "taken" ? "Steal it for" : "Buy for"} <FapsAmount n={square.minBidCents} size={13} gold={false} tone="ink" />
                    </span>
                  )
                : `${square.status === "taken" ? "Steal it for" : "Take this square —"} ${centsLabel(
                    square.minBidCents,
                  )}`}
        </button>
      )}
      {/* The doors out of a short balance open the wallet ON TOP of this dialog. This
          dialog is owned by DisplayApp, not the menu, so it may own one (GoldDialog's
          nested-AuthModal precedent); when it closes the balance is re-read and the
          form returns on its own. */}
      {fapsOpen ? <FapsDialog source="wall_short" focus={fapsOpen} onClose={() => setFapsOpen(null)} /> : null}
      {goldOpen ? <GoldDialog source="faps" onClose={() => setGoldOpen(false)} /> : null}
      {/* The rules, exactly as the server enforces them: pay-to-place, no refunds, hold
          until outbid, a takeover price that starts high and decays.

          Deliberately no multiplier and no day count — the call the old wall intro
          made, and the wall guide (guides.tsx) still makes. Both numbers are settings (takeoverMultiplier,
          decayDays), so spelling either one into a sentence dates the copy to one
          configuration and quietly lies the moment it is tuned. The SHAPE is the part a
          buyer can act on, and it survives a price change.

          Nothing vague reaches the amount actually charged: the button above quotes it
          in cents, computed server-side by wallTakeoverCents. */}
      {free ? (
        <p className="mt-2 text-xs leading-snug text-zinc-500">
          This one's on the house — its price drifted all the way down, so the first taker
          gets it free. No card, no checkout. Your square shows your clips under your name
          until somebody outbids you — and taking it off you costs real money from the
          moment you claim, dearest at first, drifting down again the longer you hold it.
          One free square a day per account.
        </p>
      ) : faps ? (
        <p className="mt-2 text-xs leading-snug text-zinc-500">
          Paid from your Faps — no card, no checkout, the square is yours the moment you buy.
          It shows your clips under your name until somebody outbids you; what it costs to
          take it off you rides demand: dearest just after it changes hands, drifting down
          the longer you hold it. No refunds when outbid: the time on the wall is what you
          bought.
        </p>
      ) : (
        <p className="mt-2 text-xs leading-snug text-zinc-500">
          One card payment, handled by our processor. Your square shows your clips under your
          name until somebody outbids you — what it costs to take it off you rides demand:
          dearest just after it changes hands, drifting down the longer you hold it. No
          refunds when outbid: the time on the wall is what you bought.
        </p>
      )}
      {error ? <p className="mt-2 text-xs text-red-400">{error}</p> : null}
    </>
  );
}


/**
 * The holder's move: defend a square you already own by re-buying it at its current
 * takeover price, which resets the decay clock so a sniper pays the full premium again.
 * A pure Faps sink. No clip picker — the square keeps its content; only the price moves.
 * The cost is the board's live `minBidCents`, the same number a stealer would pay; the
 * server recomputes it and is the authority, so a stale badge can only over- or
 * under-quote, never mischarge.
 */
function DefendPanel({
  regionId,
  square,
  onClose,
}: {
  regionId: string;
  square: WallSquareState;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const balance = useFapsStore((s) => s.wallet?.balance ?? null);
  const cost = square.minBidCents;
  const short = balance !== null && balance < cost;
  const submit = async () => {
    if (busy || short) return;
    setBusy(true);
    setError(null);
    try {
      const r = await userApi.wallDefend(regionId);
      // Balance verbatim + a quiet wallet re-read; the board repaints from the server's
      // own pushConfig, so there is nothing to poll here.
      useFapsStore.getState().applyStop({ balance: r.fapsBalance });
      onClose();
    } catch (e) {
      setError((apiErrorOf(e)?.error ?? "Couldn't defend — try again.").replace(/^\d+:\s*/, ""));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <p className="mb-3 text-sm text-zinc-300">
        Re-buy your square to reset its price — snipers pay the full premium again. Your clips stay put.
      </p>
      <button
        type="button"
        onClick={submit}
        disabled={busy || short}
        className="w-full rounded-lg bg-[#f5c518] px-4 py-2.5 text-sm font-semibold text-black hover:brightness-110 disabled:opacity-50"
      >
        {busy ? (
          "Defending…"
        ) : (
          <span className="inline-flex items-center justify-center gap-1">
            Defend for <FapsAmount n={cost} size={13} gold={false} tone="ink" />
          </span>
        )}
      </button>
      {short ? (
        <p className="mt-2 text-xs text-zinc-400">
          You&rsquo;re <FapsAmount n={cost - (balance ?? 0)} size={11} gold={false} /> short.
        </p>
      ) : null}
      {error ? <p className="mt-2 text-xs text-red-400">{error}</p> : null}
    </div>
  );
}
